/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useEffectEvent } from '#hooks/PRIVATE_useEffectEvent';
import { DataState, ItemActionDetail } from '../UNSAFE_Collection';
import { Item } from '../utils/UNSAFE_dataProvider';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { isControlOrFunctionKey, KEYS } from '../utils/PRIVATE_keyboardUtils';
import {
  isBeforeDataFetch,
  isPhone,
  isSearchTextEmptyOrUndefined,
  useSelectCommon
} from '../PRIVATE_SelectCommon';
import { SelectSingle } from './SelectSingle';

type PickedPropsFromSelectSingle = Pick<
  ComponentProps<typeof SelectSingle>,
  | 'addToList'
  | 'advancedSearch'
  | 'isDisabled'
  | 'isReadonly'
  | 'onAddToListAction'
  | 'onAdvancedSearchAction'
  | 'onFilter'
>;
type UseSelectSingleProps<K, D> = PickedPropsFromSelectSingle & {
  data?: DataState<K, D> | null;
  inputRef: Ref<HTMLInputElement>;
  isFocused?: boolean;
  onCommit: (detail: ValueUpdateDetail<K>) => void;
  valueItem?: Item<K, D>;
};

/**
 * Hook that manages SelectSingle state and behavior.  This hook creates state variables and
 * event listeners, returning properties to apply to components internally rendered by
 * SelectSingle, as well as state information.
 *
 * @param data Specifies data for the dropdown list.
 * @param inputRef Ref to the input element.
 * @param isDisabled Specifies whether the component is disabled.
 * @param isFocused Specifies whether the component has focus.
 * @param isReadonly Specifies whether the component is readonly.
 * @param onCommit Callback invoked when the selected value is committed.
 * @param onFilter Callback function to trigger loading data for the dropdown list,
 * which may or may not be filtered by user entered text.
 * @param valueItem Specifies the key, data, and optional metadata for the selected value.
 *
 * @returns Properties to apply to internal components that SelectSingle renders, and component
 * state.
 */
export function useSelectSingle<K, D>({
  addToList,
  advancedSearch,
  data,
  inputRef,
  isDisabled,
  isFocused: propIsFocused,
  isReadonly,
  onAddToListAction,
  onAdvancedSearchAction,
  onCommit: propOnCommit,
  onFilter,
  valueItem
}: UseSelectSingleProps<K, D>) {
  const {
    currentRow,
    dropdownRef,
    getDropdownTabbableElems,
    handleDropdownArrowClick,
    handleDropdownAutoDismiss: baseHandleDropdownAutoDismiss,
    handleDropdownPosition,
    handleInput,
    handleMainFieldKeyDown: baseHandleMainFieldKeyDown,
    handleMainFieldKeyUp,
    isDropdownAbove,
    isDropdownOpen,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    onCurrentKeyChange,
    onMouseDown,
    searchText,
    setCurrentRow,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    userInput
  } = useSelectCommon<K, D>({
    data,
    inputRef,
    isFocused: propIsFocused,
    onFilter
  });

  const [selectedKeys, setSelectedKeys] = useState<Set<K>>();
  const [isBlurClearValue, setBlurClearValue] = useState(false);
  const [prevIsFocused, setPrevIsFocused] = useState(isFocused);
  const [isTabbingOutOfDropdown, setTabbingOutOfDropdown] = useState(false);

  // refs used by some of the effects below to determine whether the effect needs to run,
  // for when the list of dependencies is longer than what we want the trigger to actually be
  const prevDataRef = useRef(data);
  const prevDropdownOpenRef = useRef(isDropdownOpen);
  const prevSearchText = useRef(searchText);

  const isAdvancedSearchShown =
    advancedSearch === 'on' && isUserFiltering && searchText != null && searchText.length > 0;
  const isDataFetched = data !== undefined && data !== null && !isBeforeDataFetch<K, D>(data);
  const isEmptyResults = isDataFetched && data?.totalSize === 0;
  // advanced search and add to list should not be shown at the same time, so if both are on,
  // let advanced search win because it is shown more often than add to list in the dropdown
  // (even when the list is not empty) and its external UI could potentially include the
  // ability to add to list too
  const isAddToListShown =
    addToList === 'on' &&
    advancedSearch !== 'on' &&
    isUserFiltering &&
    searchText != null &&
    searchText.length > 0 &&
    isEmptyResults;

  const handleAddToListAction = useCallback(() => {
    setDropdownOpen(false);
    stopFiltering();
    onAddToListAction?.({ searchText: isUserFiltering ? searchText : undefined });
  }, [isUserFiltering, onAddToListAction, searchText, setDropdownOpen, stopFiltering]);

  const handleAdvancedSearchAction = useCallback(() => {
    setDropdownOpen(false);
    stopFiltering();
    onAdvancedSearchAction?.({ searchText: isUserFiltering ? searchText : undefined });
  }, [isUserFiltering, onAdvancedSearchAction, searchText, setDropdownOpen, stopFiltering]);

  const onCommit = useCallback(
    (value?: K) => {
      propOnCommit?.({
        value: value,
        previousValue: valueItem !== undefined ? valueItem.key : undefined
      });
    },
    [propOnCommit, valueItem]
  );

  const clearValue = useCallback(
    (shouldCloseDropdown = true) => {
      if (shouldCloseDropdown) {
        setDropdownOpen(false);
      }

      setSelectedKeys(new Set<K>());

      // call the onCommit callback so that the consuming code can update the valueItem
      onCommit(undefined);
    },
    [onCommit, setDropdownOpen]
  );

  // determine if we need to clear the value because the user has deleted all the text
  const isClearValueNeeded = useCallback(() => {
    return isUserFiltering && userInput === '' && valueItem !== undefined;
  }, [isUserFiltering, userInput, valueItem]);

  const clearValueIfNeeded = useCallback(() => {
    if (isClearValueNeeded()) {
      clearValue();
      return true;
    }
    return false;
  }, [clearValue, isClearValueNeeded]);

  // JET-65757 - Empty value rejected when not confirmed by Enter/Tab
  // When the field loses focus, check to see if we need to clear the value and set a state flag
  // to later check in a useEffect.
  // (We have to make this check outside of the useEffect itself because by the time the useEffect
  // is called, the state we check here to see if we need to clear the value may have already
  // been changed.)
  if (
    prevIsFocused &&
    !isFocused &&
    !isTabbingOutOfDropdown &&
    !isBlurClearValue &&
    isClearValueNeeded()
  ) {
    setBlurClearValue(true);
  }
  // Create this function with useEffectEvent so that it is stable and by itself won't cause
  // the below dependent useEffect to trigger.
  const handleBlur_stable = useEffectEvent(() => {
    // if we need to clear the value, do it now and reset the flag
    if (isBlurClearValue) {
      clearValue();
      setBlurClearValue(false);
    }
    // if we were tabbing out of the dropdown, reset the flag now that we've lost focus
    if (isTabbingOutOfDropdown) {
      setTabbingOutOfDropdown(false);
    }
  });
  // only run this effect when focus changes (handleBlur_stable has to be listed
  // as a dependency, but it's stable and won't change across renders)
  useEffect(() => {
    if (!isFocused) {
      handleBlur_stable();
    }
  }, [handleBlur_stable, isFocused]);

  // JET-60376 - Evaluate unit test driving strategy for SelectSingle
  // Add a _changeValue method to the root DOM element so that a test adapter can call it
  // to simulate the user changing the value, similar to what we do in the core pack select
  // component test adapters.
  const textFieldRef = useRef<HTMLElement>();
  const changeValue = useCallback(
    (value?: K) => {
      // we always want this method available on the DOM element so that a test can call it,
      // but we only want it to change the value when the component is enabled because that's
      // the only time the user could change the value through the UI
      if (!isReadonly && !isDisabled) {
        onCommit(value);
        return Promise.resolve();
      }
      return Promise.reject('Component is readonly or disabled');
    },
    [isDisabled, isReadonly, onCommit]
  );
  useEffect(() => {
    const ref = isReadonly ? mainFieldRef : textFieldRef;
    if (ref.current) {
      (ref.current as any)._changeValue = changeValue;
    }
  }, [changeValue, isReadonly, mainFieldRef]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const removeDropdown = useCallback(() => {
    dropdownRef.current?.parentElement?.removeChild(dropdownRef.current);
  }, [dropdownRef]);

  const myHandleDropdownAutoDismiss = useCallback(
    (event?: Event) => {
      if (isDropdownOpen) {
        if (event?.type === 'keydown') {
          const keyboardEvent = event as KeyboardEvent;
          switch (keyboardEvent.code) {
            case KEYS.TAB:
              // if there are multiple tabbable elements in the dropdown, like the list and
              // the advanced search link, then leave the dropdown open when tabbing among them
              const isShift = keyboardEvent.shiftKey;
              const tabbableElems = getDropdownTabbableElems();
              if (tabbableElems && tabbableElems.length > 1) {
                const firstTabbableElem = tabbableElems[0];
                const lastTabbableElem = tabbableElems[tabbableElems.length - 1];
                // check if tabbing forwards and focus is not on the last tabbable elem or
                // tabbing backwards and focus is not on the first tabbable elem,
                // then keep the dropdown open and simply return
                if (
                  (!isShift && document.activeElement !== lastTabbableElem) ||
                  (isShift && document.activeElement !== firstTabbableElem)
                ) {
                  setDropdownOpen(true);
                  return;
                }
              }

              // if there is a currentRow in the dropdown, select it and commit it
              if (
                currentRow !== undefined &&
                currentRow.rowKey !== undefined &&
                data != null &&
                data.data.length > 0
              ) {
                setSelectedKeys(new Set<K>([currentRow.rowKey]));
                onCommit(currentRow.rowKey);

                // set flag that we're tabbing out of dropdown so that we can check it in a
                // useEffect that gets executed when we lose focus
                setTabbingOutOfDropdown(true);
              }

              // Put focus on an element in the main field, but don't kill the event, so that
              // the browser can naturally transfer focus to the next focusable item relative
              // to the element in the main field.
              focusInput();

              // Remove the dropdown immediately, without waiting for a rerender based on changing
              // state, so that the open dropdown cannot get focus when the browser handles the
              // Tab key.
              removeDropdown();
              break;
            default:
              break;
          }
        }
      }
    },
    [
      currentRow,
      data,
      focusInput,
      getDropdownTabbableElems,
      isDropdownOpen,
      onCommit,
      removeDropdown,
      setDropdownOpen
    ]
  );

  const myHandleMainFieldKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // ignore control key and function key
      if (isControlOrFunctionKey(event)) {
        return;
      }

      switch (event.code) {
        case KEYS.TAB:
          // if we're not going to clear the value when the component loses focus,
          // check whether we need to select the current item
          const isValueClearedTab = isClearValueNeeded();
          if (!isValueClearedTab && isDropdownOpen && data != null && data.data.length > 0) {
            // if there is a currentRow in the dropdown (meaning a highlighted item that would
            // have keyboard focus if the dropdown list had physical focus, which may be different
            // from the currently selected item), select it and commit it;
            // otherwise commit the currently selected value again so that a valueAction event
            // will be fired
            if (currentRow !== undefined && currentRow.rowKey !== undefined) {
              setSelectedKeys(new Set<K>([currentRow.rowKey]));
              onCommit(currentRow.rowKey);
            } else {
              onCommit(valueItem?.key ?? undefined);
            }
          }

          // Remove the dropdown immediately, without waiting for a rerender based on changing
          // state, so that the open dropdown cannot get focus when the browser handles the
          // Tab key.
          removeDropdown();

          setDropdownOpen(false);
          break;

        case KEYS.ENTER:
          // if this is on a mobile device (only phone and not in tablet devices), do not do anything
          if (isPhone()) {
            return;
          }

          // if add to list is shown, there are no results, so trigger the add to list link
          if (isAddToListShown) {
            handleAddToListAction();
            return;
          }

          // if advanced search is shown and there are no results, trigger the advanced
          // search link
          if (isAdvancedSearchShown && isEmptyResults) {
            handleAdvancedSearchAction();
            return;
          }

          // check whether we need to clear the value;
          // if not, check whether we need to select the current item
          const isValueCleared = clearValueIfNeeded();
          if (!isValueCleared && isDropdownOpen && data != null && data.data.length > 0) {
            setDropdownOpen(false);

            // after selecting a value, the user is no longer actively filtering, so update related
            // state accordingly so that the newly selected value appears in the field
            stopFiltering();

            // if there is a currentRow in the dropdown, select it and commit it;
            // otherwise commit the currently selected value again
            if (currentRow !== undefined && currentRow.rowKey !== undefined) {
              setSelectedKeys(new Set<K>([currentRow.rowKey]));
              onCommit(currentRow.rowKey);
            } else {
              onCommit(valueItem?.key ?? undefined);
            }
          }
          break;

        default:
          break;
      }
    },
    [
      clearValueIfNeeded,
      currentRow,
      data,
      handleAddToListAction,
      handleAdvancedSearchAction,
      isAddToListShown,
      isAdvancedSearchShown,
      isClearValueNeeded,
      isDropdownOpen,
      isEmptyResults,
      onCommit,
      removeDropdown,
      setDropdownOpen,
      stopFiltering,
      valueItem
    ]
  );

  // when the valueItem changes, save the new selectedKeys
  useEffect(() => {
    setSelectedKeys(valueItem === undefined ? undefined : new Set([valueItem.key]));
  }, [valueItem]);

  const onItemAction = useCallback(
    (detail: ItemActionDetail<K, D>) => {
      focusInput();
      setDropdownOpen(false);

      // after selecting a value, the user is no longer actively filtering, so update related
      // state accordingly so that the newly selected value appears in the field
      stopFiltering();

      const newKey = detail.context.metadata.key;
      // update the selectedKeys so that the selection visual state of the collection
      // updates immediately while waiting for a new valueItem to be set
      setSelectedKeys(new Set([newKey]));
      // call the onCommit callback so that the consuming code can update the valueItem
      onCommit(newKey);
    },
    [focusInput, onCommit, setDropdownOpen, stopFiltering]
  );

  useEffect(() => {
    // only run this effect when data changes because we need to wait for the new set of
    // filtered data to be set before highlighting the first result
    if (data === prevDataRef.current) {
      return;
    }

    // when filtering, set the first result of the filtered data set to be the current item
    // (check the data offset so we don't try to highlight again if the user scrolls through
    // the list and more data is fetched)
    if (
      isDropdownOpen &&
      isUserFiltering &&
      data != null &&
      data.offset === 0 &&
      data.data.length > 0
    ) {
      // if there are results and search text, highlight the first row, otherwise clear
      // an existing highlight
      setCurrentRow({
        rowKey: !isSearchTextEmptyOrUndefined(searchText) ? data.data[0].metadata.key : undefined
      });
    }
  }, [data, isDropdownOpen, isUserFiltering, searchText, setCurrentRow]);

  useEffect(() => {
    // only run this effect when the state of the dropdown changes
    if (isDropdownOpen === prevDropdownOpenRef.current) {
      return;
    }

    // JET-66783 - <oj-c-select-single> scroll position is not at selected item when we open the list second time.
    // set the current item to the selected item (if there is one)
    // when the dropdown opens if there is no filter text
    if (
      isDropdownOpen &&
      (!isUserFiltering || searchText === undefined || searchText.length === 0)
    ) {
      setCurrentRow({ rowKey: [...(selectedKeys ?? [undefined])][0] });
    }
  }, [isDropdownOpen, isUserFiltering, searchText, selectedKeys, setCurrentRow]);

  const dropdownEventHandlers = useMemo(() => {
    return mergeProps(
      {
        onAutoDismiss: baseHandleDropdownAutoDismiss,
        onPosition: handleDropdownPosition
      },
      { onAutoDismiss: myHandleDropdownAutoDismiss }
    );
  }, [baseHandleDropdownAutoDismiss, handleDropdownPosition, myHandleDropdownAutoDismiss]);

  const inputEventHandlers = useMemo(() => {
    return mergeProps(
      {
        onInput: handleInput,
        onKeyDown: baseHandleMainFieldKeyDown,
        onKeyUp: handleMainFieldKeyUp
      },
      { onKeyDown: myHandleMainFieldKeyDown }
    );
  }, [baseHandleMainFieldKeyDown, handleInput, handleMainFieldKeyUp, myHandleMainFieldKeyDown]);

  if (isFocused !== prevIsFocused) {
    setPrevIsFocused(isFocused);
  }

  // update refs used in some of the above effects in a useEffect on every render so that they
  // update after the above effects run
  useEffect(() => {
    prevDataRef.current = data;
    prevDropdownOpenRef.current = isDropdownOpen;
    prevSearchText.current = searchText;
  });

  return {
    addToListEventHandlers: {
      onAction: handleAddToListAction
    },
    advancedSearchEventHandlers: {
      onAction: handleAdvancedSearchAction
    },
    collectionProps: {
      currentKey: currentRow.rowKey,
      onCurrentKeyChange,
      onItemAction,
      selectedKeys
    },
    dropdownArrowEventHandlers: {
      onClick: handleDropdownArrowClick
    },
    clearValue,
    dropdownEventHandlers,
    dropdownRef,
    inputEventHandlers,
    inputRef,
    isAddToListShown,
    isAdvancedSearchShown,
    isDataFetched,
    isDropdownAbove,
    isDropdownOpen,
    isEmptyResults,
    isFocused,
    isUserFiltering,
    mainFieldRef,
    mouseProps: isDisabled || isReadonly ? {} : { onMouseDown },
    searchText,
    setDropdownOpen,
    setUserInput,
    stopFiltering,
    textFieldRef,
    userInput
  };
}
